import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Globe, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import KnowledgeDropzone from '@/components/chatbot/KnowledgeDropzone';

/**
 * Knowledge base tab — documents & API sources.
 * Extracted from ChatbotDetailPage.jsx during the iter132.2 split so the
 * parent page is under 1,700 lines and adding a new source type (e.g.
 * "Notion", "Confluence") is a one-file change here instead of a diff on
 * the monolith.
 *
 * The parent still owns state + handlers so we don't fork ownership of the
 * knowledge-base data model — this component is pure presentation.
 */
export const KnowledgeTab = ({
  documents,
  apiSources,
  uploading,
  refreshingKnowledge,
  setRefreshingKnowledge,
  loadKnowledgeBase,
  handleFileUpload,
  handleViewDocument,
  handleDeleteDocument,
  setShowSitemapDialog,
  setShowTextDocDialog,
  setShowAPISourceDialog,
  getStatusColor,
}) => {
  return (
    <>
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-slate-900">Documents ({documents.length})</CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSitemapDialog(true)}
              className="border-slate-300 text-slate-700"
              data-testid="import-website-btn"
            >
              <Globe className="w-4 h-4 mr-1" />Import from Website
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTextDocDialog(true)}
              className="border-slate-300 text-slate-700"
              data-testid="add-text-doc-btn"
            >
              <Plus className="w-4 h-4 mr-1" />Add Text
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                setRefreshingKnowledge(true);
                try {
                  await loadKnowledgeBase();
                  toast.success('Documents refreshed');
                } catch { /* toast already shown from loader */ }
                finally { setRefreshingKnowledge(false); }
              }}
              disabled={refreshingKnowledge}
              className="border-slate-300 text-slate-700"
              title="Refresh documents"
              data-testid="knowledge-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshingKnowledge ? 'animate-spin text-blue-600' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Drag-and-drop zone — live MIME validation so users see
              exactly what the server will accept BEFORE the round-trip. */}
          <KnowledgeDropzone
            uploading={uploading}
            onFileAccepted={(file) => {
              handleFileUpload({ target: { files: [file], value: '' } });
            }}
          />

          {documents.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 mb-2">No documents yet</p>
              <p className="text-sm text-slate-500">Upload files, add URLs, or add content directly</p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc.document_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200"
                >
                  <div className="flex items-center gap-3">
                    {doc.source_type === 'url' ? (
                      <Globe className="w-5 h-5 text-blue-600" />
                    ) : (
                      <FileText className="w-5 h-5 text-slate-500" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                      <p className="text-xs text-slate-500">
                        {doc.document_type}
                        {doc.word_count
                          ? ` • ${doc.word_count} words`
                          : (doc.status === 'ready' || doc.status === 'active' ? '' : ' • Processing...')}
                        {doc.source_url && <span className="ml-2 text-blue-600">URL source</span>}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getStatusColor(doc.status)}>{doc.status}</Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleViewDocument(doc)}
                      className="text-slate-500 hover:text-slate-900"
                      title="View document"
                      data-testid={`doc-row-view-btn-${doc.document_id}`}
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteDocument(doc)}
                      className="text-slate-500 hover:text-red-600"
                      data-testid={`doc-row-delete-btn-${doc.document_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-slate-900">API Data Sources ({apiSources.length})</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAPISourceDialog(true)}
            data-testid="add-api-source-btn"
            className="border-slate-300 text-slate-700"
          >
            <Plus className="w-4 h-4 mr-1" />Add API
          </Button>
        </CardHeader>
        <CardContent>
          {apiSources.length === 0 ? (
            <div className="text-center py-8">
              <Globe className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 mb-2">No API sources configured</p>
              <p className="text-sm text-slate-500">Connect external APIs for real-time data</p>
            </div>
          ) : (
            <div className="space-y-2">
              {apiSources.map((source) => (
                <div
                  key={source.source_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200"
                >
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{source.name}</p>
                      <p className="text-xs text-slate-500">
                        {source.method} • Refreshes every {source.refresh_interval_minutes} min
                      </p>
                    </div>
                  </div>
                  <Badge className={source.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                    {source.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default KnowledgeTab;
